import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert, Empty, Spin, Typography } from 'antd'
import { BellOutlined } from '@ant-design/icons'
import * as api from '../lib/api'
import { formatDateTime } from '../lib/dateFormat'
import { useI18n } from '../i18n/I18nProvider'

const { Title, Text } = Typography

export default function NotificationDashboard({ embedded = false, onNavigate }) {
  const { t } = useI18n()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let ignore = false
    setLoading(true)
    setError(null)
    api.getNotifications()
      .then(result => {
        if (ignore) return
        setData(result)
        const unseenIds = (result.groups || [])
          .flatMap(group => group.items || [])
          .filter(item => !item.is_seen)
          .map(item => item.id)
        if (unseenIds.length) {
          api.markNotificationsSeen(unseenIds)
            .then(() => {
              if (ignore) return
              window.dispatchEvent(new CustomEvent('notifications.updated', { detail: { unread_total: 0 } }))
              setData(current => ({
                ...current,
                unread_total: 0,
                groups: (current?.groups || []).map(group => ({
                  ...group,
                  unread_count: 0,
                  items: (group.items || []).map(item => ({ ...item, is_seen: true, seen_at: item.seen_at || new Date().toISOString() }))
                }))
              }))
            })
            .catch(() => { })
        }
      })
      .catch(e => {
        if (!ignore) setError(e.message || t('notifications.messages.load_failed', 'Unable to load notifications'))
      })
      .finally(() => {
        if (!ignore) setLoading(false)
      })
    return () => {
      ignore = true
    }
  }, [t])

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
  if (error) return <Alert type='error' message={error} showIcon />

  const notifications = (data?.groups || [])
    .flatMap(group => group.items || [])
    .slice()
    .sort((left, right) => {
      const rightTime = new Date(right.created_at).getTime()
      const leftTime = new Date(left.created_at).getTime()
      if (rightTime !== leftTime) return rightTime - leftTime
      return (Number(right.id) || 0) - (Number(left.id) || 0)
    })
  const notificationLinkProps = url => onNavigate
    ? {
      onClick: event => {
        event.preventDefault()
        onNavigate(url)
      }
    }
    : {}

  return (
    <div>
      {!embedded && (
        <Title level={4} className='tti-page-title' style={{ marginBottom: 4 }}>
          <BellOutlined /> {t('notifications.title', 'Notifications')}
        </Title>
      )}

      {notifications.length === 0
        ? <Empty description={t('notifications.empty', 'No pending notifications')} style={{ margin: '50px 48px' }} />
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 24 }}>
            {notifications.map(item => (
              <div
                key={item.id}
                onClick={() => {
                  if (onNavigate) onNavigate(item.action_url)
                }}
                role='button'
                tabIndex={0}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    if (onNavigate) onNavigate(item.action_url)
                  }
                }}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 4px',
                  borderBottom: '1px solid var(--neutral-grey2)'
                }}
              >
                <BellOutlined style={{ color: 'var(--tti-grey)', fontSize: 16, flexShrink: 0 }} />
                <div style={{ minWidth: 0 }}>
                  <div>
                    <Link to={item.action_url} {...notificationLinkProps(item.action_url)} style={{ color: 'inherit', fontWeight: 600 }}>
                      {item.title}
                    </Link>
                  </div>
                  <Text type='secondary' style={{ fontSize: 12 }}>{formatDateTime(item.created_at)}</Text>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  )
}
